'use strict';

/**
 * Shared helpers for event registration forms (fullEvent / onSiteEvent).
 * Keep flow-specific logic in the respective entrypoints.
 */

const SELECTORS = {
    // Must be a select: bootstrap-select copies classes onto its wrapper div
    phonePrefixSelect: 'select.js-select-prefix',
    addressWrapper: '.js-address-wrapper',
    isForeigner: '.js-is-foreigner'
};

const ITALIAN_PREFIX = '+39';

/**
 * Finds the .js-address-wrapper that follows the phone row.
 * @param {JQuery} $prefixSelect - Prefix select element
 * @returns {JQuery} Related address wrapper
 */
function getAddressWrapper($prefixSelect) {
    return $prefixSelect.closest('.row').nextAll('.js-address-wrapper').first();
}

/**
 * Finds the isForeigner hidden input near a phone prefix select.
 * @param {JQuery} $prefixSelect - Prefix select element
 * @returns {JQuery} Related hidden input
 */
function getIsForeignerInput($prefixSelect) {
    return $prefixSelect.closest('.cc-form__column-prefix').find(SELECTORS.isForeigner);
}

/**
 * Clears visible validation errors inside the address wrapper.
 * @param {JQuery} $wrapper - Address wrapper
 */
function clearAddressErrors($wrapper) {
    $wrapper.find('.form-control.cc-invalid, .is-invalid').removeClass('cc-invalid is-invalid');
    $wrapper.find('.form-control, .js-form-control, .js-form-control-disabled').removeAttr('aria-invalid');
    $wrapper.find('.invalid-feedback').removeClass('cc-message--visible').text('');
    $wrapper.find('.cc-form__wrap--error').removeClass('cc-form__wrap--error');
}

/**
 * Empties address fields so they are not posted when the prefix is foreign.
 * @param {JQuery} $wrapper - Address wrapper
 */
function clearAddressFields($wrapper) {
    $wrapper.find('input, select, textarea').each(function () {
        const $field = $(this);

        if ($field.is('select')) {
            $field.val('');
            if ($field.hasClass('selectpicker') && typeof $field.selectpicker === 'function') {
                $field.selectpicker('val', '');
                $field.selectpicker('refresh');
            }
        } else {
            $field.val('').attr('value', '');
        }
    });
}

/**
 * Enables or disables client-side validation on address fields.
 * Uses the same class swap as fullEvent/eventHelper (js-form-control / js-select-validate).
 * @param {JQuery} $wrapper - Address wrapper
 * @param {Boolean} enabled - Whether validation should be active
 */
function toggleAddressValidation($wrapper, enabled) {
    const $form = $wrapper.closest('form');
    const formName = $form.attr('name');
    const fieldNames = [];

    $wrapper.find('.js-form-wrap').each(function () {
        const $wrap = $(this);
        if ($wrap.data('addressRequiredWrapInit') !== true) {
            $wrap.data('addressRequiredWrap', $wrap.hasClass('required') || $wrap.hasClass('cc-required'));
            $wrap.data('addressRequiredWrapInit', true);
        }

        if (enabled && $wrap.data('addressRequiredWrap')) {
            $wrap.addClass('required cc-required');
        } else {
            $wrap.removeClass('required cc-required');
        }
    });

    $wrapper.find('input, select, textarea').each(function () {
        const $field = $(this);
        const fieldName = $field.attr('name');

        if (fieldName) {
            fieldNames.push(fieldName);
        }

        if ($field.data('addressRequiredInit') !== true) {
            $field.data('addressRequired', Boolean($field.prop('required')));
            $field.data('addressRequiredInit', true);
        }

        if (enabled) {
            if ($field.data('addressRequired')) {
                $field.attr('required', true).attr('aria-required', 'true');
            }

            if ($field.hasClass('js-form-control-disabled')) {
                $field.removeClass('js-form-control-disabled').addClass('js-form-control');
            }
            if ($field.hasClass('js-select-validate-disabled')) {
                $field.removeClass('js-select-validate-disabled').addClass('js-select-validate');
            }
            if ($field.hasClass('js-form-select-disabled')) {
                $field.removeClass('js-form-select-disabled').addClass('js-form-select');
            }
        } else {
            $field.removeAttr('required').removeAttr('aria-required');

            if ($field.hasClass('js-form-control')) {
                $field.removeClass('js-form-control').addClass('js-form-control-disabled');
            }
            if ($field.hasClass('js-select-validate')) {
                $field.removeClass('js-select-validate').addClass('js-select-validate-disabled');
            }
            if ($field.hasClass('js-form-select')) {
                $field.removeClass('js-form-select').addClass('js-form-select-disabled');
            }
        }
    });

    if (!enabled) {
        clearAddressFields($wrapper);
        clearAddressErrors($wrapper);
        if (formName && fieldNames.length) {
            $('body').trigger('formValidation:clearFields', [formName, fieldNames]);
        }
    }

    // Re-check remaining visible fields so submit can enable
    const $probe = $form.find('input.js-form-control, select.js-select-validate').filter(':visible').first();
    if ($probe.length) {
        $probe.trigger('change');
    }
}

/**
 * Shows or hides the address block and updates isForeigner from the phone prefix.
 * @param {JQuery} $prefixSelect - Prefix select element
 */
function toggleAddressByPrefix($prefixSelect) {
    if (!$prefixSelect.is('select')) {
        return;
    }

    let value = String($prefixSelect.val() || '').trim();
    // Fallback to selected option / default italian prefix
    if (!value) {
        value = String($prefixSelect.find('option:selected').val() || ITALIAN_PREFIX);
        $prefixSelect.val(value);
    }

    const isItalian = value === ITALIAN_PREFIX;
    const $wrapper = getAddressWrapper($prefixSelect);
    const $isForeigner = getIsForeignerInput($prefixSelect);

    if ($isForeigner.length) {
        const foreignerValue = isItalian ? 'false' : 'true';
        $isForeigner.val(foreignerValue).attr('value', foreignerValue);
    }

    if (!$wrapper.length) {
        return;
    }

    if (isItalian) {
        $wrapper.removeClass('d-none');
    } else {
        $wrapper.addClass('d-none');
    }

    toggleAddressValidation($wrapper, isItalian);
}

/**
 * Syncs all prefix selects currently in the DOM.
 * @param {JQuery|HTMLElement|String} [container=document] - Scope to sync
 */
function syncAddressVisibility(container) {
    const $selects = $(container).find(SELECTORS.phonePrefixSelect);
    $selects.each(function () {
        const $select = $(this);
        toggleAddressByPrefix($select);
        // Ensure prefix is registered in formValidation validFieldsCount
        $select.trigger('change');
    });
}

/**
 * Binds prefix change listeners and applies the initial visibility state.
 */
function init() {
    $('body')
        .off('change.eventRegistrationPrefix changed.bs.select.eventRegistrationPrefix')
        .on(
            'change.eventRegistrationPrefix changed.bs.select.eventRegistrationPrefix',
            SELECTORS.phonePrefixSelect,
            function () {
                toggleAddressByPrefix($(this));
            }
        );

    syncAddressVisibility(document);
}

module.exports = {
    init: init,
    syncAddressVisibility: syncAddressVisibility,
    toggleAddressByPrefix: toggleAddressByPrefix
};
